import React, { createContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'utils/getTranslations';

// third-party
import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js';

// action - state management
import { switchTrackedTimecard } from 'store/reducers/timecard.ts';

// project imports
import Loader from 'components/Loader';
import { AWS_API } from 'config';
import { useImpersonationSync } from 'hooks/useImpersonationSync';
import { usePrivilegesSync } from 'hooks/usePrivilegesSync';
import { AlreadyEnrolledError, AuthContextType, BackendAuthStatus, UserProfile } from 'types/auth';
import { cognitoUser } from 'utils/cognitoUser';

// Utils
import { clearAllResumeRecords } from 'pages/ai-chat/aiChatResume';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorage';
import { clearRecentlyViewed } from 'utils/recentlyViewed';

// Store
import { useDispatch } from 'store';
import { portalAPI } from 'store/api/portalAPI';
import { APIUserSlice } from 'store/api/user';
import type { UserData } from 'types/user.ts';
import { errorAlert } from 'utils/alert';
import { dropImpersonationDesign } from 'utils/impersonationDesign';
import { logError } from 'utils/monitoring';
import { observePrivilegesVersionHeader } from 'utils/privilegesVersion';

const userPool = new CognitoUserPool({
    UserPoolId: AWS_API.poolId || '',
    ClientId: AWS_API.appClientId || '',
});

const MFA_SETUP_REQUIRED = 'MFA_SETUP_REQUIRED';

export type AuthContextUserData = UserData & {
    name: string;
    activeTenantId: string;
};

type AuthStateType = {
    isLoggedIn: boolean;
    isInitialized: boolean;
    cognitoUserProfile: UserProfile | null;
    user: AuthContextUserData | null;
};

const toAuthContextUserData = (user: UserData): AuthContextUserData => ({
    ...user,
    name: user.firstName + ' ' + user.lastName,
    activeTenantId: user.tenants[0],
});

// ==============================|| AWS Cognito CONTEXT & PROVIDER ||============================== //

const AWSCognitoContext = createContext<AuthContextType | null>(null);

export const AWSCognitoProvider = ({ children }: { children: React.ReactElement }): JSX.Element => {
    const [authState, setAuthState] = useState<AuthStateType>({
        isLoggedIn: false,
        isInitialized: false,
        cognitoUserProfile: null,
        user: null,
    });
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render gate `if (... || isLoading) return <Loader />`
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const reduxDispatch = useDispatch();

    // TODO(react-doctor: no-cascading-set-state): auth init flow — distinct
    // state machines coordinated through this single mount-time effect.
    useEffect(() => {
        const init = async () => {
            try {
                const username = getLocalStorage('cognito_user');
                if (!username) {
                    setAuthState({
                        isLoggedIn: false,
                        isInitialized: true,
                        cognitoUserProfile: null,
                        user: null,
                    });
                    return;
                }
                const user = await setAndFetchUserData(username);
                setAuthorisedState(
                    {
                        email: username,
                    },
                    user
                );
            } catch (err) {
                console.error(err);
                setAuthState({
                    isLoggedIn: false,
                    isInitialized: true,
                    cognitoUserProfile: null,
                    user: null,
                });
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setAndFetchUserData, setAuthorisedState, setAuthState omitted: all defined below this effect via `const`, so listing them as deps would TDZ. This is a mount-once cognito-bootstrap effect by design.
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        // Cognito User instance to use for the authentication
        const cognitoLogin = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        const authData = new AuthenticationDetails({
            Username: email,
            Password: password,
        });

        try {
            await new Promise<boolean>((resolve, reject) => {
                cognitoLogin.authenticateUser(authData, {
                    onSuccess: (session: CognitoUserSession) => {
                        cognitoLogin.getUserData((err, cognitoData) => {
                            if (err) {
                                console.error('User authentication error: ', err);
                                reject(err);
                            }

                            if (cognitoData) {
                                authenticateInBackend(cognitoLogin, session, email)
                                    .then((status) => resolve(routeAfterBackendAuth(status)))
                                    .catch((err) => reject(err));
                            }
                        });
                    },
                    onFailure: (_err) => {
                        const isNotAuthorized = _err.code === 'NotAuthorizedException';
                        const isUserDisabled = _err.message.toLowerCase().includes('user is disabled');

                        if (isNotAuthorized && isUserDisabled) {
                            const customErrorMessage = t('auth.user-disabled');
                            console.error(customErrorMessage);
                            reject(new Error(customErrorMessage));
                        } else {
                            console.error('Login error: ', _err);
                            reject(_err);
                        }
                    },

                    totpRequired: () => {
                        cognitoUser.setCognitoUser(cognitoLogin);
                        cognitoUser.setSignInEmail(email);
                        navigate('/mfa-verification');
                    },
                    newPasswordRequired: ({ email_verified, email, ...attrs }) => {
                        cognitoUser.setCognitoUser(cognitoLogin);
                        cognitoUser.setSignInEmail(email);
                        cognitoUser.setSessionUserAttributes(attrs);
                        navigate('/set-password');
                    },
                });
            });
        } catch (e) {
            console.error('Authentication error: ', e);
            if (e instanceof Error) {
                throw e;
            }
        }
    };

    const azureLogin = async (email: string, jwt: string): Promise<void> => {
        if (!email || !jwt) {
            console.error('Missing email or JWT for Azure SSO login');
            throw new Error('Azure SSO login error');
        }

        // Cognito User instance to use for the authentication
        const cognitoLogin = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        // Custom object to identify the authentication flow in the Cognito Lambda trigger
        const clientMetadata = {
            authFlow: 'AZURE_SSO',
        };

        const authData = new AuthenticationDetails({
            Username: email,
            Password: '',
            ClientMetadata: clientMetadata,
        });

        try {
            setIsLoading(true);
            cognitoLogin.setAuthenticationFlowType('CUSTOM_AUTH');

            await new Promise<boolean>((resolve, reject) => {
                cognitoLogin.initiateAuth(authData, {
                    onSuccess: (session: CognitoUserSession) => {
                        cognitoLogin.getUserData((err, cognitoData) => {
                            if (err) {
                                console.error('User authentication error: ', err);
                                reject(err);
                            }

                            if (cognitoData) {
                                authenticateInBackend(cognitoLogin, session, email)
                                    .then((status) => resolve(routeAfterBackendAuth(status)))
                                    .catch((err) => reject(err));
                            }
                        });
                    },
                    totpRequired: () => {
                        cognitoUser.setCognitoUser(cognitoLogin);
                        cognitoUser.setSignInEmail(email);
                        navigate('/mfa-verification');
                    },
                    onFailure: (_err) => {
                        console.log('Login error: ', _err);
                        reject(_err);
                    },
                    customChallenge: function () {
                        // User authentication depends on challenge response
                        // send credentials to Cognito VerifyAuthChallenge lambda trigger for verification
                        cognitoLogin.sendCustomChallengeAnswer(jwt, this, clientMetadata);
                    },
                });
            });
        } catch (e) {
            console.error('Authentication error: ', e);
            if (e instanceof Error) {
                throw e;
            } else {
                throw new Error('Authentication error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // This function is not used in the app
    // User registration is started from the portal when adding new employee
    const register = (email: string, password: string, firstName: string, lastName: string) =>
        new Promise((success, rej) => {
            userPool.signUp(
                email,
                password,
                [
                    new CognitoUserAttribute({ Name: 'email', Value: email }),
                    new CognitoUserAttribute({ Name: 'name', Value: `${firstName} ${lastName}` }),
                ],
                [],
                (err, result) => {
                    if (err) {
                        rej(err);
                        return;
                    }
                    success(result);
                }
            );
        });

    const logout = () => {
        try {
            removeLocalStorage('cognito_user');
            clearAllResumeRecords();
            clearRecentlyViewed();
            dropImpersonationDesign();
            setAuthState({
                isLoggedIn: false,
                isInitialized: true,
                cognitoUserProfile: null,
                user: null,
            });
            window.location.href = import.meta.env.VITE_API_URL + '/logout';
        } catch (error) {
            console.error(error);
            logError(error);
        }
    };

    const setInitialPassword = async (newPassword: string): Promise<BackendAuthStatus> => {
        const cognitoLogin = cognitoUser.getCognitoUser();
        const sessionUserAttributes = cognitoUser.getSessionUserAttributes();

        if (!cognitoLogin || !sessionUserAttributes) {
            throw new Error('Password challenge session expired');
        }

        try {
            return await new Promise<BackendAuthStatus>((resolve, reject) => {
                // Pass no attributes: Cognito rejects writes to the immutable
                // custom:tenant_id_immutable attribute, and we have no required
                // mutable attributes to set during the initial password flow.
                cognitoLogin.completeNewPasswordChallenge(
                    newPassword,
                    {},
                    {
                        onSuccess: function (session) {
                            void (async () => {
                                try {
                                    const status = await authenticateInBackend(
                                        cognitoLogin,
                                        session,
                                        cognitoUser.getSignInEmail() ?? cognitoLogin.getUsername()
                                    );
                                    // The enrolment screen still needs this Cognito session; only a
                                    // finished sign-in lets go of it.
                                    if (status === 'authenticated') {
                                        cognitoUser.clear();
                                    }
                                    resolve(status);
                                } catch (err) {
                                    reject(err as Error);
                                }
                            })();
                        },
                        onFailure: function (err) {
                            console.error('completeNewPasswordChallenge error: ', err);
                            reject(err);
                        },
                    }
                );
            });
        } catch (error) {
            console.error('Handle new password error: ', error);
            throw error;
        }
    };

    // Send the verification code to the user email
    const sendResetPasswordCode = async (email: string) => {
        const user = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        try {
            await new Promise((resolve, reject) => {
                user.forgotPassword({
                    onSuccess: function (data) {
                        // successfully initiated reset password request
                        console.log('CodeDeliveryData from forgotPassword: ', data);
                        resolve(data);
                    },
                    onFailure: function (err) {
                        console.error('Password reset error: ', err);
                        reject(err);
                    },
                });
            });
        } catch (e) {
            if (e instanceof Error) {
                throw e;
            }
        }
    };

    const resetPassword = async (email: string, verificationCode: string, newPassword: string) => {
        const user = new CognitoUser({
            Username: email,
            Pool: userPool,
        });

        try {
            const res: string = await new Promise((resolve, reject) => {
                user.confirmPassword(verificationCode, newPassword, {
                    onSuccess: function (data) {
                        // successfully changed password
                        resolve(data);
                    },
                    onFailure: function (err) {
                        console.error('Password reset error: ', err);
                        reject(err);
                    },
                });
            });

            return res || '';
        } catch (e) {
            if (e instanceof Error) {
                throw e;
            } else {
                console.error('Unknown error occurred in resetPassword: ', e);
                return ''; // return a default value
            }
        }
    };

    /**
     * Trades the Cognito session for a portal session. A 403 carrying MFA_SETUP_REQUIRED is the
     * backend saying the password was fine but the tenant wants an authenticator enrolled first: the
     * Cognito session is kept (enrolment runs against it) and no portal session exists yet.
     */
    const authenticateInBackend = async (
        cognitoLogin: CognitoUser,
        session: CognitoUserSession,
        username: string
    ): Promise<BackendAuthStatus> => {
        try {
            // A plain-text body and no custom header keep this a simple CORS request: no preflight.
            const res = await fetch(import.meta.env.VITE_API_URL + '/v1/auth/cognito', {
                method: 'POST',
                body: session.getIdToken().getJwtToken(),
                credentials: 'include',
            });
            if (res.status === 403) {
                const body = (await res.json().catch(() => null)) as { code?: string; tenantName?: string } | null;
                if (body?.code === MFA_SETUP_REQUIRED) {
                    cognitoUser.setCognitoUser(cognitoLogin);
                    cognitoUser.setSignInEmail(username);
                    cognitoUser.setMfaSetupTenantName(body.tenantName);
                    return 'mfaSetupRequired';
                }
            }
            if (res.status === 401 || res.status === 403) {
                throw new Error(t('auth.incorrect-credentials'));
            }
            if (!res.ok) {
                throw new Error(t('auth.login-service-unavailable'));
            }
            const json = (await res.json()) as { redirectUrl?: string };
            if (json.redirectUrl && json.redirectUrl.startsWith(import.meta.env.VITE_API_URL + '/oauth2/authorize')) {
                await setAndFetchUserData(username);
                cognitoLogin.signOut(() => {});
                window.location.href = json.redirectUrl;
                return 'authenticated';
            }
            const user = await setAndFetchUserData(username);
            setAuthorisedState({ email: username }, user);
            // The portal session cookie carries the sign-in from here; the Cognito tokens are dropped.
            cognitoLogin.signOut(() => {});
            return 'authenticated';
        } catch (err) {
            console.error('User authentication error: ', err);
            if (err instanceof Error) {
                errorAlert(err.message);
            }
            throw err;
        }
    };

    /**
     * Sends a sign-in that the backend stopped for enrolment to the enrolment screen. The login page's state
     * travels along, so the return link survives the enrolment.
     */
    const routeAfterBackendAuth = (status: BackendAuthStatus): boolean => {
        if (status === 'mfaSetupRequired') {
            navigate('/mfa-setup', { state: location.state as unknown });
        }
        return true;
    };

    const setAuthorisedState = (cognitoUserProfile: UserProfile, user: UserData) => {
        setAuthState({
            isLoggedIn: true,
            isInitialized: true,
            user: toAuthContextUserData(user),
            cognitoUserProfile: cognitoUserProfile,
        });
    };

    const setAndFetchUserData = async (username: string) => {
        return new Promise<UserData>((resolve, reject) => {
            // Ask the user data from the DB
            fetch(import.meta.env.VITE_API_URL + '/v1/users/current', {
                credentials: 'include',
            })
                .then((res) => {
                    observePrivilegesVersionHeader(res.headers);
                    if (res.ok) {
                        return res.json() as Promise<UserData>; // Proceed with parsing the JSON response
                    } else {
                        //cookie is expired or is not set
                        removeLocalStorage('cognito_user');
                        clearAllResumeRecords();
                        clearRecentlyViewed();
                        dropImpersonationDesign();
                        setAuthState({
                            isLoggedIn: false,
                            isInitialized: true,
                            cognitoUserProfile: null,
                            user: null,
                        });
                        throw new Error('User fetch failed');
                    }
                })
                .then((user) => {
                    // Store the cognito_user value to instantiate the CognitoUser object after the page refresh
                    setLocalStorage('cognito_user', username);
                    // Populate RTK Query cache so other components don't need to refetch
                    reduxDispatch(APIUserSlice.util.upsertQueryData('getUserData', undefined, user));
                    // Store the user to the context
                    if (user) {
                        reduxDispatch(switchTrackedTimecard(user));
                    }
                    resolve(user);
                })
                .catch((err) => {
                    console.error('User authentication error: ', err);
                    reject(err);
                });
        });
    };

    const setupTOTP = async (): Promise<string> => {
        const _cognitoUser = cognitoUser.getCognitoUser();
        if (!_cognitoUser) {
            throw new Error('User is not logged in, cannot setup MFA');
        }
        return new Promise((resolve, reject) => {
            _cognitoUser.associateSoftwareToken({
                associateSecretCode: (secretCode) => {
                    resolve(secretCode);
                },
                onFailure: (err) => {
                    reject(err);
                },
            });
        });
    };

    const verifyTOTP = async (code: string): Promise<void> => {
        const _cognitoUser = cognitoUser.getCognitoUser();
        if (!_cognitoUser) {
            throw new Error('Invalid state, user should be initialized');
        }
        return new Promise((resolve, reject) => {
            _cognitoUser.verifySoftwareToken(code, 'Authenticator app', {
                onSuccess: () => {
                    _cognitoUser.setUserMfaPreference(
                        null,
                        {
                            Enabled: true,
                            PreferredMfa: true,
                        },
                        (err) => {
                            if (err) {
                                console.error('Error setting TOTP as preferred MFA:', err);
                                reject(err);
                            } else {
                                resolve();
                            }
                        }
                    );
                },
                onFailure: (err) => {
                    reject(err);
                },
            });
        });
    };

    /**
     * Finishes a sign-in the backend stopped for enrolment: the authenticator was just verified with
     * Cognito, so the same Cognito session is handed to the backend again and now passes the gate.
     */
    const completeMfaSetup = async (): Promise<void> => {
        const _cognitoUser = cognitoUser.getCognitoUser();
        const session = _cognitoUser?.getSignInUserSession();
        if (!_cognitoUser || !session) {
            throw new Error('Sign-in session expired');
        }
        const status = await authenticateInBackend(
            _cognitoUser,
            session,
            cognitoUser.getSignInEmail() ?? _cognitoUser.getUsername()
        );
        if (status !== 'authenticated') {
            throw new Error('Two-factor authentication is still required');
        }
    };

    const reauthenticate = async (password: string): Promise<void> => {
        const email = getLocalStorage('cognito_user');
        if (!email) {
            throw new Error('User is not logged in');
        }
        const cognitoLogin = new CognitoUser({ Username: email, Pool: userPool });
        const authData = new AuthenticationDetails({ Username: email, Password: password });
        return new Promise<void>((resolve, reject) => {
            cognitoLogin.authenticateUser(authData, {
                onSuccess: () => {
                    cognitoUser.setCognitoUser(cognitoLogin);
                    resolve();
                },
                // A code challenge means an authenticator is already active; only support can replace it.
                totpRequired: () => {
                    cognitoLogin.signOut(() => {});
                    reject(new AlreadyEnrolledError());
                },
                onFailure: (err) => reject(err),
                newPasswordRequired: () => reject(new Error('Password change required')),
            });
        });
    };

    /** Drops a Cognito session held only for enrolment; the portal session cookie is untouched. */
    const discardCognitoSession = () => {
        cognitoUser.getCognitoUser()?.signOut(() => {});
        cognitoUser.clear();
    };

    const sendOTP = async (code: string): Promise<void> => {
        const _cognitoUser = cognitoUser.getCognitoUser();
        if (!_cognitoUser) {
            throw new Error('Invalid state, user should be initialized');
        }

        return new Promise((resolve, reject) => {
            _cognitoUser.sendMFACode(
                code,
                {
                    onSuccess: (session: CognitoUserSession) => {
                        _cognitoUser.getUserData((err, cognitoData) => {
                            if (err) {
                                console.error('User authentication error: ', err);
                                reject(err);
                            }

                            if (cognitoData) {
                                _cognitoUser.getUserAttributes((err, result) => {
                                    if (err) {
                                        console.error(err);
                                        reject('Could not get user attributes to get user email');
                                    }
                                    if (result) {
                                        const email = result
                                            .filter((attribute) => {
                                                return attribute.Name === 'email';
                                            })[0]
                                            .getValue();
                                        authenticateInBackend(_cognitoUser, session, email)
                                            .then((status) => {
                                                routeAfterBackendAuth(status);
                                                resolve();
                                            })
                                            .catch((err) => reject(err));
                                    }
                                });
                            }
                        });
                    },
                    onFailure: (err) => {
                        reject(err);
                    },
                },
                'SOFTWARE_TOKEN_MFA'
            );
        });
    };

    const impersonateUser = (userData: UserData) => {
        setAuthState((prev) => ({
            ...prev,
            user: toAuthContextUserData(userData),
        }));
        reduxDispatch(switchTrackedTimecard(userData));
        // Same reason as the cache reset below, for the per-user state that lives in localStorage
        // rather than in a query: the previous identity's conversation is not this one's to resume,
        // the records it opened are not this one's to be shown, and a design chosen for an
        // impersonation ends with it.
        clearAllResumeRecords();
        clearRecentlyViewed();
        dropImpersonationDesign();
        // Identity changed: drop every cached query so per-user data (portfolio summary,
        // dashboards, etc.) is refetched. Avoids depending on each mutation's invalidatesTags
        // list staying in sync with new per-user slices.
        reduxDispatch(portalAPI.util.resetApiState());
    };

    // Another tab switched the shared session via (stop-)impersonation: apply the new
    // identity here too so the impersonation ribbon and permissions stay truthful.
    useImpersonationSync({ isLoggedIn: authState.isLoggedIn, user: authState.user, applySessionUser: impersonateUser });

    // Same identity, different permissions or settings: replace the user the permission and setting hooks
    // read and the cached current-user query, but keep every other cached query — nothing else changed hands.
    const refreshSessionUser = (userData: UserData) => {
        setAuthState((prev) => (prev.user ? { ...prev, user: toAuthContextUserData(userData) } : prev));
        reduxDispatch(APIUserSlice.util.upsertQueryData('getUserData', undefined, userData));
    };

    // The backend moved the tenant's privileges version: a role or a role assignment changed, so
    // re-read what this session may do now.
    usePrivilegesSync({ isLoggedIn: authState.isLoggedIn, user: authState.user, applySessionUser: refreshSessionUser });

    const updateProfile = () => {};

    if ((authState.isInitialized !== undefined && !authState.isInitialized) || isLoading) {
        return <Loader />;
    }

    return (
        <AWSCognitoContext.Provider
            value={{
                ...authState,
                login,
                azureLogin,
                logout,
                register,
                sendResetPasswordCode,
                resetPassword,
                updateProfile,
                setInitialPassword,
                setupTOTP,
                verifyTOTP,
                completeMfaSetup,
                sendOTP,
                reauthenticate,
                discardCognitoSession,
                impersonateUser,
                refreshSessionUser,
            }}
        >
            {children}
        </AWSCognitoContext.Provider>
    );
};

export default AWSCognitoContext;
